/**
 * Chatbot Component
 * Main chatbot interface with messages, input, and suggestions
 */

import { useRef, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { currentUserAtom } from '@/hooks/use-auth';
import { EmptyState } from './empty-state';
import { MessageBubble } from './message-bubble';
import { SuggestedQueries } from './suggested-queries';
import { ChatInput } from './chat-input';
import { ChannelSeparator } from './channel-separator';
import { DEFAULT_SUGGESTED_QUERIES } from '../constants';
import type { ChatbotProps, Message } from '../types';
import type { AttachmentMeta } from '@/types/websocket.types';
import { Skeleton } from '@/components/ui/skeleton';

export const Chatbot = ({
  className,
  suggestions = DEFAULT_SUGGESTED_QUERIES,
  onSendMessage,
  onChannelChange,
  messages = [],
  isLoading = false,
  thinkingStep,
  availableChannels = [],
  selectedChannelId
}: ChatbotProps) => {
  const [currentUser] = useAtom(currentUserAtom);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  const handleSendMessage = (message: string, attachments?: AttachmentMeta[]) => {
    onSendMessage?.(message, selectedChannelId, attachments);
    // Hide suggestions after first message
    if (messages.length === 0) {
      setShowSuggestions(false);
    }
  };

  const handleSelectQuery = (query: string) => {
    handleSendMessage(query);
  };

  const handleToggleSuggestions = () => {
    setShowSuggestions(!showSuggestions);
  };

  const handleChannelChange = (channelId: string) => {
    console.log('handleChannelChange', channelId);
    onChannelChange?.(channelId);
  };

  // Group messages by channel to show separators
  const renderMessages = () => {
    const messagesWithSeparators: (Message | { type: 'separator'; channelName: string; role: string })[] = [];
    let lastChannelId: string | undefined = undefined;

    messages.forEach((message: Message) => {
      if (message.channelId && message.channelId !== lastChannelId && lastChannelId !== undefined) {
        // Find channel name
        const channel = availableChannels.find((ch) => ch.id === message.channelId);
        messagesWithSeparators.push({
          type: 'separator',
          channelName: channel?.name || 'Unknown Channel',
          role: 'assistant'
        });
      }
      messagesWithSeparators.push(message);
      lastChannelId = message.channelId;
    });

    return messagesWithSeparators.map((item, index) => {
      if ('type' in item && item.type === 'separator') {
        return <ChannelSeparator key={`separator-${index}`} channelName={item.channelName} />;
      }
      return <MessageBubble key={(item as Message).id} message={item as Message} />;
    });
  };

  const hasMessages = messages.length > 0;

  return (
    <div className={cn('flex flex-col h-full w-full mx-auto', 'min-w-[300px] max-w-[650px]', className)}>
      {/* Messages Area */}
      <div className='flex-1 overflow-hidden'>
        <ScrollArea ref={scrollAreaRef} className='h-full w-full'>
          <div className='px-4 py-5'>
            {!hasMessages ? <EmptyState className='text-sm leading-snug' username={currentUser?.name} /> : <div className='space-y-1'>{renderMessages()}</div>}

            {/* Loading Indicator */}
            {isLoading && (
              <div className='flex items-start mb-4 flex-col'>
                <div className='text-xs font-medium text-muted-foreground mb-1.5 ml-1'>Kim AI</div>
                <div
                  className='px-4 py-3 rounded-2xl border text-sm flex flex-col gap-2.5 min-w-[220px] max-w-[550px]'
                  style={{
                    backgroundColor: 'var(--color-gray-50)',
                    borderColor: 'var(--color-gray-100)'
                  }}
                >
                  <div className='flex items-center gap-2'>
                    <span className='size-2 rounded-full bg-indigo-400 animate-pulse shrink-0' />
                    <span className='text-xs text-muted-foreground font-medium'>
                      {thinkingStep || 'Processing...'}
                    </span>
                  </div>
                  <Skeleton
                    className='w-full h-2.5 rounded-full'
                    style={{ background: 'linear-gradient(90deg, var(--color-indigo-100) 0%, var(--color-purple-100) 100%)' }}
                  />
                  <Skeleton
                    className='w-4/5 h-2.5 rounded-full'
                    style={{ background: 'linear-gradient(90deg, var(--color-indigo-100) 0%, var(--color-purple-100) 100%)' }}
                  />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Bottom Section */}
      <div className='shrink-0 px-4 pb-1 space-y-3'>
        {/* Suggested Queries */}
        {showSuggestions && !hasMessages && (
          <SuggestedQueries queries={suggestions} onSelectQuery={handleSelectQuery} />
        )}

        {/* Chat Input */}
        <ChatInput
          onSend={handleSendMessage}
          onToggleSuggestions={handleToggleSuggestions}
          disabled={isLoading}
          selectedChannelId={selectedChannelId}
          availableChannels={availableChannels}
          onChannelChange={handleChannelChange}
        />

        {/* Context Info */}
        <p className='text-center text-[10px] text-muted-foreground'>
          This Playground is a conversational flow that retains context.
        </p>
      </div>
    </div>
  );
};
